import React, { useMemo } from 'react';

import { OfferType } from 'constants/enums';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { CircleFadingPlus } from 'lucide-react';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { OffersFilterId } from '../../OfferFilters.types';
import { useOfferTable } from '../../hooks/useOfferTable';
import { OfferPageLayout } from '../../components/OfferPageLayout';
import RollingOfferForm from '../Form/RollingOfferForm';

import { CheatsDialog } from './components/CheatsDialog';
import { useRollingOfferCheats } from './hooks/useRollingOfferCheats';
import { useRollingOffersColumns } from './hooks/useRollingOffersColumns';

const RollingOffersTable = (): React.ReactElement => {
  const { isPageDisabled } = usePageTypeByFeatureFlag('Rolling');

  const {
    getOffers,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    hasActiveFilters,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    updateCollectedIndex,
    offers,
    processOffers
  } = useOfferTable({
    offerType: OfferType.ROLLING_OFFER,
    offerLabel: 'Rolling Offer',
    needsPricings: false
  });

  const { cheatsOfferId, hasFeatureFlagCheats, onOpenCheats, handleCloseCheats } =
    useRollingOfferCheats();

  const { columns } = useRollingOffersColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    hasFeatureFlagCheats,
    onOpenCheats
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  if (isPageDisabled) {
    return <RollingOfferForm />;
  }

  return (
    <OfferPageLayout
      title="Rolling Offers"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Rolling Offers"
      showFilters={offers.length > 0}
      hasActiveFilters={hasActiveFilters}
      filterBarConfig={{
        hiddenFilters: [OffersFilterId.OFFER_DESIGN, OffersFilterId.PRICE]
      }}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Rolling Offer"
      emptyStateDescription={
        <>
          Rolling Offers unlock new sub-offers as players progress.
          Purchasing or
          <br />
          collecting an offer reveals the next, motivating ongoing engagement
          before
          <br />
          the promotion resets.
        </>
      }
      createButtonPrefix="Rolling Offer"
      bodyDataTestId="dashRollingOfferList"
      emptyStateIcon={
        <CircleFadingPlus className="text-base-mutedForeground h-6 w-6" />
      }
      offerType={OfferType.ROLLING_OFFER}
      offerDesignType={OfferDesignTypes.RollingOffer}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Rolling Offer"
      deleteModalDescription="Are you sure you want to delete this rolling offer?"
    >
      <CheatsDialog
        cheatsOfferId={cheatsOfferId}
        updateCollectedIndex={updateCollectedIndex}
        onClose={handleCloseCheats}
      />
    </OfferPageLayout>
  );
};

export default RollingOffersTable;
